import type { TextFieldComponent } from '@getgo/chameleon-web'
import { SVG_ALERT_OUTLINED, SVG_LOADER, SVG_JOIN_OUTLINED } from '@getgo/chameleon-icons'
import { html, query, state } from 'lit-element'
import { nothing } from 'lit-html'
import { unsafeSVG } from 'lit-html/directives/unsafe-svg'
import { navigateToMeeting, validateMeetingId } from '../../common/meeting-helpers'
import { ShellElement } from '../../common/shell-element'
import { t } from '../../directives/translate'
import styles from './meeting-id-input.styles.scss'

const MEETING_ID_OR_POTENTIAL_MEETING_ID_REGEXP = /^(\d{3})-?(\d{1,3})-?(\d{1,3})?$/
const MEETING_ID_REPLACEMENT_PATTERN = `$1-$2-$3`
const MEETING_ID_UNPARSED_SECTIONS_REGEXP = /-(?:\$2|\$3)?$/

const MEETING_URL_REGEXP = /^(https?:\/\/|)(www\.|)(meet\.goto\.com|gotomeet\.me|app\.goto\.com\/meeting|)\/?/

const COULD_BE_MEETING_URL_REGEXP =
  /^(https?:\/\/|)(www\.|)(meet\.goto\.com|gotomeet\.me|app\.goto\.com\/meeting|)\/?\/[/äöüÄÖÜßa-zA-Z_\-\d]+$/

const TOO_LONG_FOR_MEETING_ID_REGEXP = /(^(\d{3})-?(\d{3})-?(\d{4,})$)/

export class GoToMeetingIdInput extends ShellElement {
  static readonly tagName = 'goto-meeting-id-input'

  @query('.meeting-id-input-field') meetingIdInputField: TextFieldComponent | undefined
  @state() private invalidMeetingId = false
  @state() private meetingLoading = false

  static get styles() {
    return styles
  }

  private isPossibleMeetingUrl(input: string) {
    return COULD_BE_MEETING_URL_REGEXP.test(input)
  }

  private isTooLongForMeetingId(value: string) {
    return TOO_LONG_FOR_MEETING_ID_REGEXP.test(value)
  }

  private formatMeetingId(input: string) {
    if (this.isTooLongForMeetingId(input)) {
      return input.replace(/-/g, '')
    }
    return input
      ?.replace(MEETING_ID_OR_POTENTIAL_MEETING_ID_REGEXP, MEETING_ID_REPLACEMENT_PATTERN)
      .replace(MEETING_ID_UNPARSED_SECTIONS_REGEXP, '')
  }

  private formatInputDisplay(input: string) {
    input = this.formatMeetingId(input)
    if (this.isPossibleMeetingUrl(input)) {
      const truncatedMeetingUrl = input.replace(MEETING_URL_REGEXP, '')
      return this.formatMeetingId(truncatedMeetingUrl)
    }
    return input
  }

  private handleMeetingIdInputButtonClick() {
    this.handleMeetingId()
  }

  private handleMeetingIdInputKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.handleMeetingId()
    } else {
      this.handleKeyDownLimitInputCharacters(e)
    }
  }

  private async handleMeetingId() {
    if (!this.meetingIdInputField?.value) {
      this.invalidMeetingId = true
      return
    }
    const meetingId = await validateMeetingId(this.meetingIdInputField.value, 'shell-landing-page')
    if (meetingId) {
      this.meetingLoading = true
      this.invalidMeetingId = false
      navigateToMeeting(meetingId)
    } else {
      this.invalidMeetingId = true
    }
  }

  private handleInput(event: { target: HTMLInputElement }) {
    const inputValue = event.target.value
    if (this.meetingIdInputField) {
      this.meetingIdInputField.value = this.formatInputDisplay(inputValue)
    }
    this.invalidMeetingId = false
  }

  private handleKeyDownLimitInputCharacters(event: KeyboardEvent) {
    if (!event.key.match(/[/äöüÄÖÜßa-zA-Z_\-\d]/)) {
      event.preventDefault()
    }
  }

  render() {
    return html`
      <div class="text-field-container">
        <chameleon-text-field
          class="meeting-id-input-field"
          placeholder="${t('Enter meeting name or ID')}"
          fieldsize="xlarge"
          fullwidth
          @keydown=${this.handleMeetingIdInputKeydown}
          @input=${this.handleInput}
          ?error=${this.invalidMeetingId}
        >
          ${this.invalidMeetingId
            ? html` <chameleon-svg class="alert-icon" slot="end">${unsafeSVG(SVG_ALERT_OUTLINED)}</chameleon-svg>`
            : nothing}
          ${this.meetingLoading
            ? html`<chameleon-icon-button size="large" variant="neutral" slot="end">
                <chameleon-svg class="loading-icon">${unsafeSVG(SVG_LOADER)}</chameleon-svg></chameleon-icon-button
              >`
            : html`
                <chameleon-tooltip-v2 slot="end" position="bottom-center">
                  <chameleon-icon-button
                    class="join-icon-button"
                    size="large"
                    slot="trigger"
                    variant="neutral"
                    @click=${this.handleMeetingIdInputButtonClick}
                  >
                    <chameleon-svg>${unsafeSVG(SVG_JOIN_OUTLINED)}</chameleon-svg>
                  </chameleon-icon-button>
                  <span slot="content">Join</span></chameleon-tooltip-v2
                >
              `}
        </chameleon-text-field>
        ${this.invalidMeetingId
          ? html` <chameleon-typography class="invalid-id-message" variant="caption-default-01" color="danger-01"
              >${t('A valid meeting name or ID is required')}</chameleon-typography
            >`
          : nothing}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToMeetingIdInput.tagName]: GoToMeetingIdInput
  }
}
